import {
  afterNextRender,
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  ElementRef,
  inject,
  Injector,
  OnInit,
  signal,
  viewChild,
} from '@angular/core';
import { ActivatedRoute, ParamMap, Router } from '@angular/router';
import { combineLatest } from 'rxjs';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { TranslatePipe } from '@ngx-translate/core';
import { KontoProfilkopfComponent } from '../components/konto-profilkopf/konto-profilkopf.component';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { MatButton } from '@angular/material/button';
import { AccountService } from './account.service';
import { UserProfileDTO } from '../models/user-profile.model';
import { KontoApiTokensComponent } from '../components/konto-api-tokens/konto-api-tokens.component';
import { ProviderTokensSectionComponent } from '../components/provider-tokens-section/provider-tokens-section.component';
import { KontoSektionComponent } from '../components/konto-sektion/konto-sektion.component';
import { GithubTokenSectionComponent } from '../components/github-token-section/github-token-section.component';
import { KiMcpBlockComponent } from './ki-mcp-block/ki-mcp-block.component';
import { BenachrichtigungenKarteComponent } from './benachrichtigungen-karte/benachrichtigungen-karte.component';
import { KONTO_REITER, KontoReiterComponent, KontoReiterId } from '../components/konto-reiter/konto-reiter.component';
import { ProviderTokenStatusService } from '../services/provider-token-status.service';

const BENACHRICHTIGUNGEN_FRAGMENT = 'benachrichtigungen';

function startReiter(params: ParamMap, fragment: string | null): KontoReiterId {
  if (params.get('tokenHost')) return 'integrationen';
  if (fragment === BENACHRICHTIGUNGEN_FRAGMENT) return 'profil';
  return KONTO_REITER.find((id) => id === params.get('tab')) ?? 'profil';
}

@Component({
  selector: 'app-account',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    TranslatePipe,
    MatProgressSpinner,
    MatButton,
    KontoApiTokensComponent,
    ProviderTokensSectionComponent,
    KontoSektionComponent,
    GithubTokenSectionComponent,
    KiMcpBlockComponent,
    BenachrichtigungenKarteComponent,
    KontoProfilkopfComponent,
    KontoReiterComponent,
  ],
  templateUrl: './account.component.html',
  styleUrl: './account.component.scss',
})
export class AccountComponent implements OnInit {
  private accountService = inject(AccountService);
  private destroyRef = inject(DestroyRef);
  private injector = inject(Injector);
  private router = inject(Router);
  private route = inject(ActivatedRoute);
  private fragment = toSignal(this.route.fragment, { initialValue: null });
  protected readonly providerTokenStatus = inject(ProviderTokenStatusService);
  private benachrichtigungenKarte = viewChild('benachrichtigungen', { read: ElementRef<HTMLElement> });

  readonly profile = signal<UserProfileDTO | null>(null);
  readonly profileLoading = signal(true);
  readonly profileError = signal(false);
  readonly reiter = signal<KontoReiterId>('profil');
  readonly tokenHost = signal<string | null>(null);

  ngOnInit(): void {
    combineLatest([this.route.queryParamMap, this.route.fragment])
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(([params, fragment]) => {
        this.reiter.set(startReiter(params, fragment));
        this.tokenHost.set(params.get('tokenHost'));
      });
    this.providerTokenStatus.laden();
    this.loadProfile();
  }

  reiterWaehlen(reiter: KontoReiterId): void {
    this.reiter.set(reiter);
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { tab: reiter, tokenHost: null },
      queryParamsHandling: 'merge',
      replaceUrl: true,
    });
  }

  loadProfile(): void {
    this.profileLoading.set(true);
    this.profileError.set(false);
    this.accountService
      .getProfile()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (profile) => {
          this.profile.set(profile);
          this.profileLoading.set(false);
          this.scrolleZumFragment();
        },
        error: () => {
          this.profileLoading.set(false);
          this.profileError.set(true);
          this.scrolleZumFragment();
        },
      });
  }

  // Profilkopf bzw. Fehlerblock über der Karte verschieben sie beim Laden; erst nach der Profil-Antwort steht sie fest.
  private scrolleZumFragment(): void {
    if (this.fragment() !== BENACHRICHTIGUNGEN_FRAGMENT) return;
    afterNextRender(() => this.benachrichtigungenKarte()?.nativeElement.scrollIntoView({ block: 'start' }), {
      injector: this.injector,
    });
  }
}
